/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot } from '@angular/router';
import { PrinterFinderListItem, REPORT_JOB_ORDER_SUMMARY, ReportService } from '@gh/builder-shared';
import {
  booleanVariable,
  JobOrderMethod,
  JobOrderRestService,
  JobOrderStatus,
  OfficeRestService, PrinterRestService,
  reportIdField,
} from '@gh/builder-shared-data';
import { DictionaryService, getRouteParam, InitViewService } from '@gh/common-shared';
import { ID } from '@gh/common-shared-data';
import { GridLayoutService } from '@gh/core-ui-grid';
import { combineLatest$, of$ } from '@gh/rx';
import { map$, switchMap$ } from '@gh/rx/operators';

import { Observable } from 'rxjs';

import {
  JOB_ORDER_LIST_EMPTY,
  JobOrderLetter,
  JobOrderList,
  jobOrderListFromDto,
  jobOrderListNoProducing,
  jobOrderListPrintAll,
  JobOrderListProduce,
  JobOrderListProduceStatus,
  jobOrderListProduceStatusFromDto,
} from './job-order-list.model';

@Injectable()
export class JobOrderDetailsListBusinessService implements InitViewService {

  constructor(private jobOrderRest: JobOrderRestService,
              private officeRest: OfficeRestService,
              private report: ReportService,
              private printerRest: PrinterRestService,
              private dictionaryService: DictionaryService,
              private gridLayoutService: GridLayoutService) {
  }

  initView(route: ActivatedRouteSnapshot): any {
    const jobId = getRouteParam(route, 'jobId');

    return {
      orderList: jobId ? this.findAllJobOrders(jobId) : this.getEmptyJobOrders(),
      sendOrderByDict: this.dictionaryService.getByEnum(JobOrderMethod),
      orderStatusDict: this.dictionaryService.getByEnum(JobOrderStatus),
      jobOrderGridLayout: this.gridLayoutService.get('jobOrders'),
      printers: this.printerRest.findAll(),
    };
  }

  getEmptyJobOrders(): Observable<JobOrderList> {
    return combineLatest$(
      this.report.generateUrl(REPORT_JOB_ORDER_SUMMARY, []),
      this.officeRest.getVariables({ printRecordForm: booleanVariable('estimating->printRecordForm') })).pipe(
      map$(([reportUrl, variables]) => ({
        ...JOB_ORDER_LIST_EMPTY,
        estimatingCriteria: {
          ...JOB_ORDER_LIST_EMPTY.estimatingCriteria,
          printRecordForm: variables.printRecordForm,
        },
        reportUrl,
      })));
  }

  findAllJobOrders(jobId: ID): Observable<JobOrderList> {
    return combineLatest$(
      this.jobOrderRest.findAllByJobId(jobId),
      this.report.generateUrl(REPORT_JOB_ORDER_SUMMARY, [reportIdField('Job', jobId)])).pipe(
      switchMap$(([list, reportUrl]) => list.job.produceInProgress
        ? this.getProduceStatus(jobId).pipe(map$((status) => jobOrderListFromDto(list, status, reportUrl)))
        : [jobOrderListFromDto(list, jobOrderListNoProducing(jobId), reportUrl)]));
  }

  preview(jobId: ID, orderId: ID): Observable<JobOrderLetter> {
    return this.jobOrderRest.preview(jobId, orderId);
  }

  produce(jobId: ID, request: JobOrderListProduce): Observable<JobOrderListProduceStatus> {
    return this.jobOrderRest.produce(jobId, request).pipe(switchMap$(() => this.getProduceStatus(jobId)));
  }

  printAllForEstimator(jobId: ID,
                       printer?: PrinterFinderListItem,
                       authCode?: string): Observable<JobOrderListProduceStatus> {
    return this.jobOrderRest.produce(jobId, jobOrderListPrintAll()).pipe(
      switchMap$(() => this.getProduceStatus(jobId)));
  }

  gets3Link(jobId: ID): Observable<any> {
    return this.jobOrderRest.getS3Link(jobId);
  }

  getProduceStatus(jobId: ID): Observable<JobOrderListProduceStatus> {
    return this.jobOrderRest.getProduceStatus(jobId).pipe(
      map$((progress) => jobOrderListProduceStatusFromDto(jobId, progress)));
  }
}
